import Bugsnag from '@bugsnag/js';
import request from '../../lib/graphql-request';
import {
  UCSAddress,
  UCSBusiness,
  UCSCoverageArea,
  UCSProvidedService,
  UCSMetrics,
} from '../../types/ucs';
import {
  Business,
  Address,
  CoverageArea,
  ProvidedService,
} from '../../types/business';
import { businessModel, metricsModel } from './query';
import { formatDataString } from '../../helpers/formatting';
import { translateKeyToDefaultLabel } from '../../services/business-registry';
import { Metrics } from '../../types/metrics-types';
import { LatLng } from '../../types/maps';

const getBusinessLatLng = (business: Business): LatLng => {
  const address = business?.primaryAddress || business?.addresses[0];
  if (!address) {
    return undefined;
  }
  return {
    lat: address.latitude,
    lng: address.longitude,
  };
};

const getFormattedAddressParts = (address: UCSAddress) => {
  return [
    [address.address1, address.address2, address.address3, address.address4]
      .filter((a) => !!a)
      .join(' '),
    `${address.city}, ${address.province} ${address.postalCode1}`,
  ].filter((a) => !!a);
};

const getFormattedPostalCode = (address: UCSAddress) => {
  return address.postalCode1;
};

const getFormattedAboutUs = (business: Business) => {
  if (business.about) {
    return business.about;
  }

  const address = business.primaryAddress || { display: { cityState: '' } };
  const businessTypesDescription = business.businessTypes?.length
    ? ` is a ${business.businessTypes
        .map((b) => formatDataString(b))
        .join(', ')} that`
    : '';
  const specialtiesDescription = business.providedServices?.length
    ? ` and specializes in ${business.providedServices
        .map((x) =>
          formatDataString(x.name).replace(/\b(hvac|bim)\b/gi, (_, m1) =>
            m1.toUpperCase()
          )
        )
        .join(', ')}.`
    : '.';
  return `${business.display.name}${businessTypesDescription} serves the ${address.display.cityState} area${specialtiesDescription}`;
};

const getFormattedAddress = (address: UCSAddress) => {
  const addressPartial = [
    address.address1,
    address.address2,
    address.address3,
    address.address4,
  ]
    .filter((a) => !!a)
    .join(' ');

  return `${addressPartial} ${address.city}, ${address.province} ${address.postalCode1}`;
};

const getFormattedCityState = (address: UCSAddress) => {
  return `${address.city}, ${address.province}`;
};

const convertUCSAddressToAddress = (ucsAddress: UCSAddress): Address => {
  return {
    address1: ucsAddress.address1,
    address2: ucsAddress.address2,
    address3: ucsAddress.address3,
    address4: ucsAddress.address4,
    city: ucsAddress.city,
    countryCode: ucsAddress.countryCode,
    id: ucsAddress.id,
    latitude: ucsAddress.latitude,
    longitude: ucsAddress.longitude,
    name: ucsAddress.name,
    postalCode1: ucsAddress.postalCode1,
    primary: ucsAddress.primary,
    province: ucsAddress.province,
    display: {
      address: getFormattedAddress(ucsAddress),
      addressParts: getFormattedAddressParts(ucsAddress),
      postalCode: getFormattedPostalCode(ucsAddress),
      cityState: getFormattedCityState(ucsAddress),
    },
  };
};

const getAddress = (business: UCSBusiness): Address => {
  if (!business.addresses?.length) {
    return null;
  }
  const primary = business.addresses.filter((address) => address.primary)[0];
  return convertUCSAddressToAddress(primary || business.addresses[0]);
};

const convertUCSCoverageAreaToCoverageArea = (
  ucsCoverageArea: UCSCoverageArea
): CoverageArea => {
  if (
    !ucsCoverageArea.admin1 &&
    !ucsCoverageArea.admin2 &&
    !ucsCoverageArea.admin3 &&
    !ucsCoverageArea.admin4
  ) {
    return null;
  }
  return {
    admin1: ucsCoverageArea.admin1 || null,
    admin2: ucsCoverageArea.admin2 || null,
    admin3: ucsCoverageArea.admin3 || null,
    admin4: ucsCoverageArea.admin4 || null,
    countryCode: ucsCoverageArea.countryCode,
    googlePlaceId: ucsCoverageArea.googlePlaceId,
    locality: ucsCoverageArea.locality,
    selectedLevel: ucsCoverageArea.selectedLevel,
  };
};

export const convertUCSMetricsToMetrics = (metrics: UCSMetrics): Metrics => {
  return {
    totalProjects: metrics.totalProjects || null,
    activeProjects: metrics.activeProjects || null,
    rfisCompleted: metrics.rfisCompleted || null,
    numberOfOpenRfi: metrics.numberOfOpenRfi || null,
    numberOfProjectsWithOpenRfi: metrics.numberOfProjectsWithOpenRfi || null,
    submittalsCompleted: metrics.submittalsCompleted || null,
    averageTimeToSubmittal: metrics.averageTimeToSubmittal || null,
    freemiumTotalProjects: metrics.freemiumTotalProjects || null,
    freemiumTotalActiveProjects: metrics.freemiumTotalActiveProjects || null,
    freemiumAvgProjectSize: metrics.freemiumAvgProjectSize || null,
    freemiumNumOfEmployees: metrics.freemiumNumOfEmployees || null,
  };
};

const covertUCSProvidedServiceToProvidedService = (
  ucsProvidedService: UCSProvidedService
): ProvidedService => {
  return {
    id: ucsProvidedService.id,
    key: ucsProvidedService.key,
    name: translateKeyToDefaultLabel(ucsProvidedService.key, ''),
    type: ucsProvidedService.level,
  };
};

const getDisplayName = (business: UCSBusiness) => {
  return business.dba?.trim() || business.name;
};

export const convertUCSBusinessToBusiness = (
  ucsBusiness: UCSBusiness
): Business => {
  const preview = {
    about: ucsBusiness.about,
    primaryAddress: getAddress(ucsBusiness),
    addresses: (ucsBusiness.addresses || []).map(convertUCSAddressToAddress),
    claimed: ucsBusiness.claimed,
    classifications: ucsBusiness.classifications,
    constructionSectors:
      ucsBusiness.constructionSectors?.map((cs) =>
        translateKeyToDefaultLabel(cs, '')
      ) || [],
    coverageAreas: (ucsBusiness.coverageAreas || [])
      .map(convertUCSCoverageAreaToCoverageArea)
      .filter(Boolean),
    dba: ucsBusiness.dba,
    id: ucsBusiness.id,
    latLng: undefined,
    logoUrl: ucsBusiness.logoUrl,
    name: ucsBusiness.name,
    phone: ucsBusiness.phone,
    primarySlug: ucsBusiness.primarySlug,
    providedServices: (ucsBusiness.providedServices || []).map(
      covertUCSProvidedServiceToProvidedService
    ),
    website: ucsBusiness.website,
    businessTypes:
      ucsBusiness.businessTypes?.map((b) =>
        translateKeyToDefaultLabel(b, '')
      ) || [],
    display: {
      name: getDisplayName(ucsBusiness),
    },
    avgContractSize: ucsBusiness.avgContractSize,
    companySize: ucsBusiness.companySize,
    visibility: ucsBusiness.visibility,
    companyId: ucsBusiness.companyId,
    monolithCreatedAt: null,
    payingAccount: null,
    createdAt: ucsBusiness.createdAt,
  };

  preview.about = getFormattedAboutUs(preview);
  preview.latLng = getBusinessLatLng(preview);

  return preview;
};

const fetchBusinessFromGraphql = async (slugName: string) => {
  if (slugName && slugName.length > 0) {
    const response: any = await request(`
    query {
      businessBySlug(slug: "${slugName.replace(/"/g, '\\"')}") {
        ${businessModel}
      }
    }
  `).catch((e) => {
      Bugsnag.notify(e);
    });
    if (response?.businessBySlug) {
      const business = convertUCSBusinessToBusiness(response.businessBySlug);
      if (business.visibility.published) {
        return business;
      }
    }
  }
  return null;
};

const fetchMetricsFromGraphql = async (id: string) => {
  if (id?.length) {
    const response: any = await request(`
    query {
      getBusinessMetrics(businessId: "${id}") {
        ${metricsModel}
      }
    }
  `).catch((e) => {
      Bugsnag.notify(e);
    });
    if (response?.getBusinessMetrics) {
      return convertUCSMetricsToMetrics(response.getBusinessMetrics);
    }
  }
  return null;
};

const fetchBusinessFromGraphqlById = async (id: string) => {
  if (id && id.length > 0) {
    const response: any = await request(`
    query {
      businessById(id: "${id}") {
        ${businessModel}
      }
    }
  `).catch((e) => {
      Bugsnag.notify(e);
    });
    if (response?.businessById) {
      const business = convertUCSBusinessToBusiness(response.businessById);
      if (business.visibility.published) {
        return business;
      }
    }
  }
  return null;
};

const convertGoogleAddressToBusinessAddress = (
  result: google.maps.places.PlaceResult,
  baseAddress?: Address
): Address => {
  if (!result.address_components || result.address_components.length === 0) {
    return baseAddress;
  }

  const address: Address = baseAddress
    ? JSON.parse(JSON.stringify(baseAddress))
    : {};
  const streetAddressParts = [];
  const addressMapping = {
    street_number: ({ long_name }) => {
      streetAddressParts.unshift(long_name);
    },
    route: ({ short_name, long_name }) => {
      // eslint-disable-next-line no-param-reassign
      if (address?.address1) {
        address.address1 = baseAddress.address1.replace(long_name, short_name);
      }
      streetAddressParts.push(short_name);
    },
    locality: ({ long_name }) => {
      address.city = long_name;
    },
    sublocality_level_1: ({ long_name }) => {
      address.city = long_name;
    },
    country: ({ short_name }) => {
      address.countryCode = short_name;
    },
    postal_code: ({ long_name }) => {
      address.postalCode1 = long_name;
    },
    administrative_area_level_1: ({ short_name }) => {
      address.province = short_name;
    },
  };

  result.address_components.forEach((ac) => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const mapping = addressMapping[ac.types?.[0]] ?? function noop() {};
    mapping(ac);
  });
  address.address1 = streetAddressParts.join(' ');
  address.latitude = result.geometry.location.lat();
  address.longitude = result.geometry.location.lng();

  return address;
};

export {
  fetchBusinessFromGraphql,
  fetchBusinessFromGraphqlById,
  fetchMetricsFromGraphql,
  convertGoogleAddressToBusinessAddress,
};
